import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Trash2, Edit2, X, Check, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Note } from "@/lib/api-client";
import { cn } from "@/lib/utils";

interface NotesPanelProps {
  notes: Note[];
  onAdd: (content: string) => Promise<void>;
  onUpdate: (noteId: number, content: string) => Promise<void>;
  onDelete: (noteId: number) => Promise<void>;
  isLoading?: boolean;
}

export function NotesPanel({ notes, onAdd, onUpdate, onDelete, isLoading }: NotesPanelProps) {
  const [newNoteContent, setNewNoteContent] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNoteContent.trim()) return;

    setIsSubmitting(true);
    try {
      await onAdd(newNoteContent.trim());
      setNewNoteContent("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEdit = (note: Note) => {
    setEditingId(note.id);
    setEditContent(note.content);
  };

  const handleUpdate = async (noteId: number) => {
    if (!editContent.trim()) return;

    setIsSubmitting(true);
    try {
      await onUpdate(noteId, editContent.trim());
      setEditingId(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (noteId: number) => {
    if (!confirm("Are you sure you want to delete this note?")) return;

    setIsSubmitting(true);
    try {
      await onDelete(noteId);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  return (
    <div className="flex h-full flex-col bg-background">
      <div className="border-b px-4 py-3 bg-muted/30">
        <h3 className="font-semibold text-sm">Notes</h3>
      </div>

      <ScrollArea className="flex-1">
        <div className="divide-y divide-border/60">
          {notes.length === 0 && !isLoading && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No notes yet. Add one below.
            </div>
          )}

          {notes.map((note) => (
            <div key={note.id} className="p-4 hover:bg-muted/30 transition-colors group relative">
              {editingId === note.id ? (
                <div className="space-y-3">
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="min-h-[80px] text-sm resize-none"
                    disabled={isSubmitting}
                    autoFocus
                  />
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingId(null)}
                      disabled={isSubmitting}
                      className="h-8"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleUpdate(note.id)}
                      disabled={isSubmitting}
                      className="h-8"
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
                       <span>{note.createdBy}</span>
                       <span>•</span>
                       <span>{formatTime(note.createdAt)}</span>
                       {note.updatedBy && <span>(edited)</span>}
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 -mr-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
                        >
                          <MoreHorizontal className="w-3 h-3" />
                          <span className="sr-only">Actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => startEdit(note)}>
                          <Edit2 className="w-3 h-3 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(note.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-3 h-3 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <p className="text-sm whitespace-pre-wrap leading-relaxed text-foreground/90">
                    {note.content}
                  </p>
                </>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="border-t p-4 bg-muted/10">
        <form onSubmit={handleAddNote} className="relative">
          <Textarea
            placeholder="Add a note..."
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            className="min-h-[44px] pr-12 py-3 resize-none bg-background shadow-sm focus-visible:ring-1"
            disabled={isSubmitting}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddNote(e);
              }
            }}
          />
          <Button
            type="submit"
            size="sm"
            className="absolute right-2 bottom-2 h-7 w-7 p-0 rounded-full"
            disabled={!newNoteContent.trim() || isSubmitting}
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
        </form>
        <p className="text-[10px] text-muted-foreground mt-2 text-right px-1">
          Press Enter to save, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
